import { GameState } from '../game/GameState';
import { PlayerPreferences } from './PlayerPreferences';

export interface TutorialStep {
  id: string;
  title: string;
  body: string;
  controls: string;
  artAsset: string;
}

export interface TutorialProgress {
  currentIndex: number;
  completedSteps: string[];
  dismissed: boolean;
}

const STORAGE_KEY = 'conquest-of-java:tutorial-progress:v1';

export const TUTORIAL_STEPS: TutorialStep[] = [
  { id: 'welcome', title: 'Rule an Island Realm', body: 'Conquest of Java is a layered grand-strategy campaign. You govern provinces, courts, armies, fleets, diplomacy, faith, research and intelligence over generations.', controls: 'Use Tab or the directional pad to move focus. Enter, Space or the south gamepad button activates the selected control.', artAsset: 'title_screen' },
  { id: 'campaign-map', title: 'Read the Campaign Map', body: 'Province colors show ownership or the active map mode. Select a province to inspect it. Double-click to open administration. Right-click a destination to order the selected army to march.', controls: 'WASD or arrow keys pan. Mouse wheel zooms. Home restores the standard view. Number keys select map modes.', artAsset: 'strategic_map_java_base' },
  { id: 'province', title: 'Build a Productive Realm', body: 'Population, food security, autonomy, corruption and buildings determine provincial output. Resolve shortages before expanding workshops or armies.', controls: 'Open Provinces from the campaign navigation. Prioritize granaries, roads, markets and resource buildings according to local conditions.', artAsset: 'event_institution' },
  { id: 'economy', title: 'Balance Treasury and Trade', body: 'Taxes fund the state, but maintenance, corruption, transport and war consume revenue. Ports and trade agreements connect local markets and move surplus goods.', controls: 'Use the Trade and Province screens to inspect price pressure, routes, imports, exports and monthly ledgers.', artAsset: 'event_cultural_assembly' },
  { id: 'armies', title: 'Prepare Before War', body: 'Armies draw manpower, supplies and treasury. Terrain, commanders, composition, morale and logistics matter before a tactical battle begins.', controls: 'Select an army marker, then right-click a province. Fortified enemies require sieges; unfortified territory can be occupied after victory.', artAsset: 'key_art_battle' },
  { id: 'navies', title: 'Command the Seas', body: 'Fleets patrol sea zones, escort trade, raid commerce, blockade ports and transport armies. Weather and monsoons can destroy an overloaded force.', controls: 'Open Naval Command to assign orders, build ships, embark armies and prepare amphibious landings.', artAsset: 'naval_battle_background' },
  { id: 'diplomacy', title: 'Shape the Balance of Power', body: 'Trust, threat, favors, treaties, subjects and strategic interests determine how other rulers respond. War needs a valid cause and peace changes legal ownership.', controls: 'Use Diplomacy to negotiate agreements, manage subjects, choose war goals and review relations.', artAsset: 'background_diplomatic_court' },
  { id: 'court', title: 'Secure the Dynasty', body: 'Rulers age, heirs inherit and estates demand influence. Succession clarity, family relationships and court approval determine whether power passes peacefully.', controls: 'Open Court to manage family, estates, councillors, incidents and succession law.', artAsset: 'background_royal_court' },
  { id: 'knowledge', title: 'Invest in Knowledge', body: 'Research accumulates monthly. Technologies require prerequisites and institutions; doctrines specialize armies and fleets; diffusion helps connected realms catch up.', controls: 'Open Knowledge to set a focus, fund scholars, adopt institutions and choose doctrines.', artAsset: 'background_grand_scriptorium' },
  { id: 'espionage', title: 'Build Intelligence Networks', body: 'Spy networks gain strength and infiltration but also heat. Operations can steal knowledge, sabotage rivals, create claims or destabilize courts, with separate success and exposure risks.', controls: 'Open the Shadow Council to establish networks, authorize operations and improve counter-intelligence.', artAsset: 'background_shadow_council' },
  { id: 'turn', title: 'Advance the Month Deliberately', body: 'Ending the month resolves construction, fleets, trade, recruitment, supply, movement, wars, courts, identities, diplomacy, intelligence, missions, events and AI decisions.', controls: 'Review alerts and active orders first. The optional confirmation prevents accidental turn advancement.', artAsset: 'event_historical_crossroads' },
  { id: 'victory', title: 'Choose Your Legacy', body: 'Campaign progress is measured through conquest, trade, dynasty, religion, survival and missions. You do not need to dominate every category to create a successful realm.', controls: 'Use Missions for guided objectives and the campaign summary for long-term progress. Press F1 whenever you need this guide again.', artAsset: 'event_mission_victory' },
];

export class TutorialManager {
  static readonly instance = new TutorialManager();
  private progress: TutorialProgress = { currentIndex: 0, completedSteps: [], dismissed: false };

  private constructor() {
    this.load();
  }

  get current() {
    return TUTORIAL_STEPS[Math.max(0, Math.min(TUTORIAL_STEPS.length - 1, this.progress.currentIndex))];
  }

  get state(): Readonly<TutorialProgress> {
    return this.progress;
  }

  shouldAutoLaunch() {
    return PlayerPreferences.instance.value.tutorialEnabled && !this.progress.dismissed && this.progress.completedSteps.length < TUTORIAL_STEPS.length;
  }

  next() {
    const current = this.current;
    if (!this.progress.completedSteps.includes(current.id)) this.progress.completedSteps.push(current.id);
    this.progress.currentIndex = Math.min(TUTORIAL_STEPS.length - 1, this.progress.currentIndex + 1);
    if (this.progress.completedSteps.length >= TUTORIAL_STEPS.length) this.progress.dismissed = true;
    this.persist();
  }

  previous() {
    this.progress.currentIndex = Math.max(0, this.progress.currentIndex - 1);
    this.persist();
  }

  goTo(index: number) {
    this.progress.currentIndex = Math.max(0, Math.min(TUTORIAL_STEPS.length - 1, index));
    this.persist();
  }

  dismiss() {
    this.progress.dismissed = true;
    this.persist();
  }

  reset() {
    this.progress = { currentIndex: 0, completedSteps: [], dismissed: false };
    this.persist();
  }

  private load() {
    if (typeof localStorage === 'undefined') return;
    try {
      const saved = JSON.parse(localStorage.getItem(STORAGE_KEY) ?? 'null') as TutorialProgress | null;
      if (saved) this.progress = { currentIndex: saved.currentIndex ?? 0, completedSteps: saved.completedSteps ?? [], dismissed: saved.dismissed ?? false };
    } catch {
      this.progress = { currentIndex: 0, completedSteps: [], dismissed: false };
    }
  }

  private persist() {
    if (typeof localStorage !== 'undefined') localStorage.setItem(STORAGE_KEY, JSON.stringify(this.progress));
    const campaign = GameState.instance.campaign as unknown as { tutorialProgress?: TutorialProgress } | undefined;
    if (campaign) campaign.tutorialProgress = { ...this.progress, completedSteps: [...this.progress.completedSteps] };
  }
}
